import React, { useEffect, useState } from 'react';
import {
  API,
  showError,
} from '../helpers';
import {
  Card,
  Grid,
  Header,
  Icon,
  Label,
  Loader,
  Segment,
  Statistic,
  Table,
  Progress,
  Divider
} from 'semantic-ui-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Legend,
  AreaChart,
  Area
} from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d', '#ffc658', '#ff7c43'];

// 格式化字节大小
const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// 格式化时间
const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'N/A';
  }
};

// 操作类型映射
const actionLabels = {
  'create': { text: '创建', color: 'green', icon: 'plus' },
  'delete': { text: '删除', color: 'red', icon: 'trash' },
  'rename': { text: '重命名', color: 'blue', icon: 'edit' },
  'update': { text: '更新', color: 'yellow', icon: 'refresh' },
  'push': { text: '推送', color: 'purple', icon: 'upload' },
  'clone': { text: '克隆', color: 'teal', icon: 'download' }
};

const GitStatsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [userStats, setUserStats] = useState([]);
  const [repoStats, setRepoStats] = useState([]);

  const loadOverview = async () => {
    try {
      const res = await API.get('/api/admin/git/stats/overview');
      if (res.data.success) {
        setOverview(res.data.data);
      } else {
        showError(res.data.error || '获取概览失败');
      }
    } catch (error) {
      console.error('Load overview error:', error);
      showError('加载概览数据失败');
    }
  };

  const loadActivityData = async () => {
    try {
      const res = await API.get('/api/admin/git/stats/activity?days=30');
      if (res.data.success) {
        setActivityData(res.data.data);
      }
    } catch (error) {
      console.error('Load activity error:', error);
    }
  };

  const loadUserStats = async () => {
    try {
      const res = await API.get('/api/admin/git/stats/users?page=1&page_size=10&sort=repo_count');
      if (res.data.success) {
        setUserStats(res.data.data || []);
      }
    } catch (error) {
      console.error('Load user stats error:', error);
    }
  };

  const loadRepoStats = async () => {
    try {
      const res = await API.get('/api/admin/git/stats/repos?page=1&page_size=10&sort=size');
      if (res.data.success) {
        setRepoStats(res.data.data || []);
      }
    } catch (error) {
      console.error('Load repo stats error:', error);
    }
  };

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      await Promise.all([
        loadOverview(),
        loadActivityData(),
        loadUserStats(),
        loadRepoStats()
      ]);
      setLoading(false);
    };
    loadAll();
  }, []);

  if (loading) {
    return (
      <Segment>
        <Loader active inline='centered'>加载统计数据中...</Loader>
      </Segment>
    );
  }

  // 准备用户仓库分布数据（饼图）
  const userDistributionData = userStats.slice(0, 8).map((user, idx) => ({
    name: user.username,
    value: user.repo_count
  }));

  // 准备仓库大小排行数据
  const repoSizeData = repoStats.slice(0, 10).map(repo => ({
    name: repo.name.length > 20 ? repo.name.substring(0, 20) + '...' : repo.name,
    size: repo.size,
    sizeFormatted: formatBytes(repo.size)
  }));

  // 准备活动趋势数据
  const activityTrendData = activityData?.daily_stats?.slice(0, 14).reverse().map(item => ({
    date: item.date?.substring(5) || '',
    新建仓库: item.new_repos || 0,
    更新仓库: item.updated_repos || 0,
    活跃用户: item.active_users || 0
  })) || [];

  // 准备小时分布数据
  const hourlyData = Object.entries(activityData?.hourly_distribution || {}).map(([hour, count]) => ({
    hour: `${hour}时`,
    活动次数: count
  }));

  return (
    <div style={{ padding: '20px 0' }}>
      {/* 统计概览卡片 */}
      <Header as='h3' dividing>
        <Icon name='chart bar' />
        <Header.Content>
          Git 统计概览
          <Header.Subheader>系统 Git 服务的全局统计数据</Header.Subheader>
        </Header.Content>
      </Header>

      <Grid columns={4} stackable style={{ marginBottom: '30px' }}>
        <Grid.Column>
          <Card fluid color='blue'>
            <Card.Content>
              <Statistic size='small'>
                <Statistic.Value>
                  <Icon name='folder' />
                  {overview?.total_repos || 0}
                </Statistic.Value>
                <Statistic.Label>总仓库数</Statistic.Label>
              </Statistic>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                <Label size='mini' color='green'>{overview?.public_repos || 0} 公开</Label>
                <Label size='mini' color='red'>{overview?.private_repos || 0} 私有</Label>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid color='green'>
            <Card.Content>
              <Statistic size='small'>
                <Statistic.Value>
                  <Icon name='users' />
                  {overview?.total_users || 0}
                </Statistic.Value>
                <Statistic.Label>总用户数</Statistic.Label>
              </Statistic>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                <Label size='mini' color='blue'>{overview?.active_users_7d || 0} 近7天活跃</Label>
              </div>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid color='orange'>
            <Card.Content>
              <Statistic size='small'>
                <Statistic.Value>
                  <Icon name='database' />
                  {formatBytes(overview?.total_size || 0)}
                </Statistic.Value>
                <Statistic.Label>总存储空间</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>

        <Grid.Column>
          <Card fluid color='purple'>
            <Card.Content>
              <Statistic size='small'>
                <Statistic.Value>
                  <Icon name='plus circle' />
                  {overview?.new_repos_7d || 0}
                </Statistic.Value>
                <Statistic.Label>近7天新建</Statistic.Label>
              </Statistic>
            </Card.Content>
          </Card>
        </Grid.Column>
      </Grid>

      <Divider />

      {/* 图表区域 */}
      <Grid columns={2} stackable>
        {/* 用户仓库分布 */}
        <Grid.Column>
          <Segment>
            <Header as='h4'>
              <Icon name='pie chart' />
              用户仓库分布
            </Header>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <PieChart>
                  <Pie
                    data={userDistributionData}
                    cx='50%'
                    cy='50%'
                    labelLine={false}
                    label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    outerRadius={100}
                    fill='#8884d8'
                    dataKey='value'
                  >
                    {userDistributionData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </Segment>
        </Grid.Column>

        {/* 仓库大小排行 */}
        <Grid.Column>
          <Segment>
            <Header as='h4'>
              <Icon name='sort amount down' />
              仓库大小排行 TOP 10
            </Header>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart
                  layout='vertical'
                  data={repoSizeData}
                  margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis type='number' tickFormatter={(value) => formatBytes(value)} />
                  <YAxis dataKey='name' type='category' width={80} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(value) => formatBytes(value)} />
                  <Bar dataKey='size' fill='#8884d8' />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Segment>
        </Grid.Column>

        {/* 活动趋势 */}
        <Grid.Column width={16}>
          <Segment>
            <Header as='h4'>
              <Icon name='line graph' />
              近14天活动趋势
            </Header>
            <div style={{ height: 300 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <AreaChart data={activityTrendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='date' />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type='monotone' dataKey='新建仓库' stackId='1' stroke='#82ca9d' fill='#82ca9d' />
                  <Area type='monotone' dataKey='更新仓库' stackId='1' stroke='#8884d8' fill='#8884d8' />
                  <Area type='monotone' dataKey='活跃用户' stackId='2' stroke='#ffc658' fill='#ffc658' />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      <Divider />

      {/* 用户排行和最近活动 */}
      <Grid columns={2} stackable>
        {/* 用户仓库数量排行 */}
        <Grid.Column>
          <Segment>
            <Header as='h4'>
              <Icon name='trophy' />
              用户仓库数量排行
            </Header>
            <Table basic='very' compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>排名</Table.HeaderCell>
                  <Table.HeaderCell>用户名</Table.HeaderCell>
                  <Table.HeaderCell>仓库数</Table.HeaderCell>
                  <Table.HeaderCell>存储大小</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {userStats.slice(0, 10).map((user, idx) => (
                  <Table.Row key={user.username}>
                    <Table.Cell>
                      {idx < 3 ? (
                        <Label circular color={['gold', 'grey', 'brown'][idx]} size='mini'>
                          {idx + 1}
                        </Label>
                      ) : idx + 1}
                    </Table.Cell>
                    <Table.Cell>
                      <strong>{user.username}</strong>
                    </Table.Cell>
                    <Table.Cell>
                      <Label size='small' color='blue'>{user.repo_count}</Label>
                    </Table.Cell>
                    <Table.Cell>{formatBytes(user.total_size)}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </Segment>
        </Grid.Column>

        {/* 最近活动 */}
        <Grid.Column>
          <Segment>
            <Header as='h4'>
              <Icon name='history' />
              最近活动
            </Header>
            <div style={{ maxHeight: 350, overflowY: 'auto' }}>
              {overview?.recent_activity?.length > 0 ? (
                overview.recent_activity.map((activity, idx) => {
                  const actionInfo = actionLabels[activity.action] || { text: activity.action, color: 'grey', icon: 'circle' };
                  return (
                    <div key={idx} style={{ 
                      padding: '10px 0', 
                      borderBottom: idx < overview.recent_activity.length - 1 ? '1px solid #eee' : 'none'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <Label size='mini' color={actionInfo.color}>
                          <Icon name={actionInfo.icon} />
                          {actionInfo.text}
                        </Label>
                        <span style={{ fontWeight: 'bold' }}>{activity.username}</span>
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '12px', color: '#666' }}>
                        <Icon name='folder' />
                        {activity.repo_name}
                      </div>
                      <div style={{ marginTop: '3px', fontSize: '11px', color: '#999' }}>
                        <Icon name='clock' />
                        {formatDateTime(activity.created_at)}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div style={{ textAlign: 'center', color: '#999', padding: '30px' }}>
                  暂无活动记录
                </div>
              )}
            </div>
          </Segment>
        </Grid.Column>
      </Grid>

      {/* 小时分布 */}
      {hourlyData.length > 0 && (
        <>
          <Divider />
          <Segment>
            <Header as='h4'>
              <Icon name='clock' />
              活动时间分布（24小时）
            </Header>
            <div style={{ height: 200 }}>
              <ResponsiveContainer width='100%' height='100%'>
                <BarChart data={hourlyData}>
                  <CartesianGrid strokeDasharray='3 3' />
                  <XAxis dataKey='hour' tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey='活动次数' fill='#82ca9d' />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Segment>
        </>
      )}
    </div>
  );
};

export default GitStatsDashboard;

